import { ref, computed, watch, nextTick } from 'vue';
import { isActiveUser, parseCivilDate, type UserItem } from '@/shared/lib';
import { useNotificationForm } from './useNotificationForm';
import { notificationApi, type NotificationEditData } from '../api/notification.api';

export type { PeriodicityMode } from './useNotificationForm';
import type { PeriodicityMode } from './useNotificationForm';

const extractErrorMessage = (error: unknown, fallback: string): string => {
  const apiMessage = (error as { response?: { data?: { message?: string } } })?.response?.data
    ?.message;
  return apiMessage ?? fallback;
};

const buildInactiveExtraMembers = (
  collaborators: NotificationEditData['collaborators'],
): UserItem[] =>
  collaborators
    .filter((c) => !isActiveUser({ status: c.status, deletedAt: c.deletedAt }))
    .map((c) => ({
      id: c.id,
      memberId: c.id,
      name: c.name,
      positions: c.positions,
      membership: { id: c.id, positions: c.positions },
      status: c.status,
      deletedAt: c.deletedAt,
    }));

export function useEditNotification(emit: (event: string, ...args: unknown[]) => void) {
  const currentNotificationId = ref<number | null>(null);
  const isLoadingData = ref(false);
  const hasChanges = ref(false);
  const extraMembers = ref<UserItem[]>([]);

  const form = useNotificationForm(emit, {
    onAfterReset: () => {
      hasChanges.value = false;
      currentNotificationId.value = null;
      extraMembers.value = [];
    },
    extraMembers,
  });

  watch(
    [
      form.title,
      form.description,
      form.periodicityMode,
      form.customDays,
      form.startDate,
      form.endDate,
      form.customDates,
      form.selectedPositions,
      form.selectedUsers,
    ],
    () => {
      hasChanges.value = true;
    },
    { deep: true },
  );

  const isDraftEnabled = computed(
    () => hasChanges.value && !form.isSaving.value && !!form.title.value.trim(),
  );

  const handleUpdate = async () => {
    if (!form.isFormValid.value || form.isSaving.value || !currentNotificationId.value) return;

    form.isSaving.value = true;
    try {
      await notificationApi.updateNotification(currentNotificationId.value, form.buildPayload(false));
      form.toast.showSuccessToast({ title: 'Notificação atualizada.', type: 'success' });
      emit('notification-updated');
      form.handleClose();
    } catch (error) {
      form.toast.showErrorToast({
        title: extractErrorMessage(error, 'Erro ao atualizar notificação. Tente novamente.'),
        type: 'error',
      });
    } finally {
      form.isSaving.value = false;
    }
  };

  const handleSaveDraft = async () => {
    if (!form.title.value.trim() || form.isSaving.value || !currentNotificationId.value) return;

    form.isSaving.value = true;
    try {
      await notificationApi.updateNotification(currentNotificationId.value, form.buildPayload(true));
      form.toast.showSuccessToast({ title: 'Notificação salva como rascunho.', type: 'success' });
      emit('notification-updated');
      form.handleClose();
    } catch (error) {
      form.toast.showErrorToast({
        title: extractErrorMessage(error, 'Erro ao salvar notificação. Tente novamente.'),
        type: 'error',
      });
    } finally {
      form.isSaving.value = false;
    }
  };

  const loadNotificationData = async (id: number) => {
    isLoadingData.value = true;
    try {
      form.resetSelection();
      const data = await notificationApi.getNotificationForEdit(id);
      currentNotificationId.value = id;
      extraMembers.value = buildInactiveExtraMembers(data.collaborators);

      form.title.value = data.title;
      form.description.value = data.description;

      if (data.periodicity !== 'CUSTOM') {
        form.periodicityMode.value = data.periodicity as PeriodicityMode;
        form.startDate.value = parseCivilDate(data.startDate);
        form.endDate.value = parseCivilDate(data.endDate);
      } else if (data.customDates?.length) {
        form.periodicityMode.value = 'CUSTOM_DATES';
        const dates = data.customDates
          .map((d) => parseCivilDate(d))
          .filter((date): date is Date => date !== null);
        form.customDates.value = dates;
        form.customDateKeys.value = dates.map(() => form.generateNextKey());
        form.customDatePickerOpen.value = dates.map(() => false);
      } else if (data.customDays && data.customDays > 0) {
        form.periodicityMode.value = 'CUSTOM_DAYS';
        form.customDays.value = data.customDays;
        form.startDate.value = parseCivilDate(data.startDate);
        form.endDate.value = parseCivilDate(data.endDate);
      } else {
        form.periodicityMode.value = 'CUSTOM_DATES';
        form.customDates.value = [null];
        form.customDateKeys.value = [form.generateNextKey()];
        form.customDatePickerOpen.value = [false];
      }

      await form.fetchPositionsAndUsers();
      await form.initializeSelections(
        data.roles.map((r) => String(r.id)),
        data.collaborators.map((c) => String(c.id)),
      );

      await nextTick();
      hasChanges.value = false;
    } catch {
      form.toast.showErrorToast({ title: 'Erro ao carregar notificação', type: 'error' });
    } finally {
      isLoadingData.value = false;
    }
  };

  return {
    ...form,
    currentNotificationId,
    isLoadingData,
    hasChanges,
    isDraftEnabled,
    handleUpdate,
    handleSaveDraft,
    loadNotificationData,
  };
}
